import { useState, useEffect } from 'react'
import { get, deleteAPI } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import { useNavigate } from 'react-router-dom'
import './Debts.scss'

interface GracePeriod {
  _id: string
  org_name?: string
  firstname?: string
  lastname?: string
  email: string
  graceUntil: string
  graceNote: string
  graceGrantedAt: string
  outstanding?: number
  debt?: number
  userModel: 'User' | 'Business'
  debtSummary: {
    totalOutstanding: number
    overdueAmount: number
    pendingAmount: number
    overdueBatchCount: number
    pendingBatchCount: number
    isBlocked: boolean
    nextDeadline: string
  }
}

function GracePeriods() {
  const { notify } = useGlobalContext()
  const navigate = useNavigate()
  const [gracePeriods, setGracePeriods] = useState<GracePeriod[]>([])
  const [loading, setLoading] = useState(true)

  function fetchGracePeriods() {
    setLoading(true)
    get('admin/debt/grace')
      .then((result: any) => {
        if (result.status === 200) {
          setGracePeriods(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch grace periods')
      })
  }

  useEffect(() => {
    fetchGracePeriods()
  }, [])

  const handleRevokeGrace = (userId: string, userModel: string) => {
    if (!confirm('Are you sure you want to revoke this grace period?')) {
      return
    }

    deleteAPI(`admin/debt/grace/${userId}?userModel=${userModel}`)
      .then((result: any) => {
        if (result.status === 200) {
          notify('success', 'Grace period revoked successfully')
          fetchGracePeriods()
        } else {
          notify('error', result.message)
        }
      })
      .catch(() => {
        notify('error', 'Failed to revoke grace period')
      })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  const getUserName = (grace: GracePeriod) => {
    if (grace.userModel === 'Business') {
      return grace.org_name || 'Unknown Business'
    }
    return grace.firstname && grace.lastname
      ? `${grace.firstname} ${grace.lastname}`
      : 'Unknown User'
  }

  const viewDebtSummary = (userId: string, userModel: string) => {
    navigate(`/admin/debt-summary/${userId}?userModel=${userModel}`)
  }

  return (
    <div className='home'>
      <div className='home_tag flex justify-between'>
        <h2>Active Grace Periods</h2>
      </div>

      <div className='home_tables'>
        <div className='left'>
          {loading ? (
            <FormLoader position='center' />
          ) : (
            <>
              {gracePeriods.length > 0 ? (
                <>
                  <div className='mobile-table'>
                    {gracePeriods.map((grace: GracePeriod) => (
                      <div className='card' key={grace._id}>
                        <div className='row flex justify-between'>
                          <h4>Name</h4>
                          <p>{getUserName(grace)}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Type</h4>
                          <p>{grace.userModel}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Email</h4>
                          <p>{grace.email}</p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Outstanding</h4>
                          <p className='failed'>
                            {formatCurrency(grace.debtSummary.totalOutstanding)}
                          </p>
                        </div>
                        <div className='row flex justify-between'>
                          <h4>Grace Until</h4>
                          <p>{formatDate(grace.graceUntil)}</p>
                        </div>
                        {grace.graceNote && (
                          <div className='row flex justify-between'>
                            <h4>Note</h4>
                            <p>{grace.graceNote}</p>
                          </div>
                        )}
                        <div className='row flex justify-between'>
                          <span
                            className='view'
                            onClick={() =>
                              viewDebtSummary(grace._id, grace.userModel)
                            }
                          >
                            View Summary
                          </span>
                          <span
                            className='view'
                            style={{ color: '#e60000' }}
                            onClick={() =>
                              handleRevokeGrace(grace._id, grace.userModel)
                            }
                          >
                            Revoke
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <table className='desktop-table'>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Email</th>
                        <th>Outstanding</th>
                        <th>Overdue</th>
                        <th>Pending</th>
                        <th>Grace Until</th>
                        <th>Note</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {gracePeriods.map((grace: GracePeriod, i: number) => (
                        <tr key={grace._id}>
                          <td>{i + 1}</td>
                          <td>{getUserName(grace)}</td>
                          <td>{grace.userModel}</td>
                          <td>{grace.email}</td>
                          <td className='failed'>
                            {formatCurrency(grace.debtSummary.totalOutstanding)}
                          </td>
                          <td>
                            {formatCurrency(grace.debtSummary.overdueAmount)}
                          </td>
                          <td>
                            {formatCurrency(grace.debtSummary.pendingAmount)}
                          </td>
                          <td>{formatDate(grace.graceUntil)}</td>
                          <td
                            style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {grace.graceNote || '-'}
                          </td>
                          <td>
                            <div className='flex' style={{ gap: '10px' }}>
                              <span
                                className='view'
                                onClick={() =>
                                  viewDebtSummary(grace._id, grace.userModel)
                                }
                              >
                                view
                              </span>
                              <span
                                className='view'
                                style={{ color: '#e60000' }}
                                onClick={() =>
                                  handleRevokeGrace(grace._id, grace.userModel)
                                }
                              >
                                revoke
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className='empty flex justify-center'>
                  <div className='empty-cover'>
                    <Icon icon='mdi:calendar-clock' />
                    <h1>No Active Grace Periods</h1>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default GracePeriods
